import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { SharedModule } from '@Shared/shared.module';
import { ChartModule, UIChart } from 'primeng/chart';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { SelectModule } from 'primeng/select';
import { TaskCycleTimeBarSeries } from '../task-cycle-time-heat-map/task-cycle-time-heat-map.component';

const CHART_COLORS = [
    '#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', '#9966FF',
    '#FF9F40', '#7BC225', '#B325C2', '#C25B25'
];

@Component({
    selector: 'task-cycle-time-bar-chart',
    templateUrl: './task-cycle-time-bar-chart.component.html',
    styleUrl: './task-cycle-time-bar-chart.component.scss',
    imports: [SharedModule, ChartModule, PaginatorModule, SelectModule]
})
export class TaskCycleTimeBarChartComponent implements OnChanges {
    @ViewChild('chart') chart: UIChart;

    @Input() monthLabels: string[] = [];
    @Input() series: TaskCycleTimeBarSeries[] = [];
    @Input() isExpanded = false;
    @Input() showXAxisLabels = true;
    @Input() showLegend = false;
    @Input() chartHeight = '226px';
    @Input() collapsedChartHeight = '250px';

    legendItems: { label: string; color: string; index: number; hidden: boolean }[] = [];

    hiddenIndexes = new Set<number>();

    first2 = 0;
    rows2 = 5;
    options = [
        { label: '5', value: 5 },
        { label: '10', value: 10 }
    ];

    // Paginate tasks (months on x-axis, one bar per task per month)
    get paginate(): boolean {
        return this.series.length > 0;
    }

    get paginatorLength(): number {
        return this.series.length;
    }

    get displaySeries(): TaskCycleTimeBarSeries[] {
        if (!this.paginate) return this.series;
        return this.series.slice(this.first2, this.first2 + this.rows2);
    }

    // Y-axis unit chosen per render: hours when all bars are under a day, otherwise days
    yAxisUnit: 'hours' | 'days' = 'days';

    chartData: any = {
        labels: [],
        datasets: []
    };

    chartOptions = {
        animation: {
            duration: 0
        },
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                mode: 'nearest',
                intersect: false,
                axis: 'x',
                usePointStyle: true,
                borderWidth: 0,
                callbacks: {
                    label: (context: any) => {
                        const days = this.yAxisUnit === 'hours' ? context.raw / 24 : context.raw;
                        const formatted = this.formatHoursToDayHour(days);
                        const name = context.dataset?.label;
                        return name ? `${name}: ${formatted}` : formatted;
                    }
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    display: true,
                    font: {
                        size: 12
                    },
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0
                }
            },
            y: {
                position: 'right',
                beginAtZero: true,
                ticks: {
                    font: {
                        size: 12
                    },
                    precision: 0,
                    callback: (value: number) => {
                        if (value === 0) return '0';
                        if (this.yAxisUnit === 'hours') return value + (value === 1 ? ' hour' : ' hours');
                        return value + (value === 1 ? ' day' : ' days');
                    }
                },
                border: {
                    display: false
                }
            }
        }
    };

    constructor() { }

    ngOnChanges(simpleChanges: SimpleChanges): void {
        if (simpleChanges['series']) {
            this.hiddenIndexes.clear();
            this.first2 = 0;
        }
        if (simpleChanges['series'] || simpleChanges['monthLabels'] || simpleChanges['isExpanded']) {
            this.updateChartData();
        }
        if (simpleChanges['isExpanded']) {
            setTimeout(() => {
                if (this.chart?.chart) {
                    this.chart.chart.resize();
                }
            }, 350);
        }
    }

    onRowsChange(): void {
        this.first2 = 0;
        this.updateChartData();
    }

    onPageChange(event: PaginatorState): void {
        this.first2 = event.first ?? 0;
        this.rows2 = event.rows ?? this.rows2;
        this.updateChartData();
    }

    updateChartData(): void {
        this.chartOptions.scales.x.ticks.display = this.showXAxisLabels;

        const source = this.displaySeries;
        const offset = this.first2;

        this.legendItems = this.showLegend
            ? source.map((s, i) => ({
                label: s.label,
                color: CHART_COLORS[(offset + i) % CHART_COLORS.length],
                index: offset + i,
                hidden: this.hiddenIndexes.has(offset + i)
            }))
            : [];

        const visible = source
            .map((s, i) => ({ series: s, color: CHART_COLORS[(offset + i) % CHART_COLORS.length], index: offset + i }))
            .filter(x => !this.hiddenIndexes.has(x.index));

        // series data is in hours; show hours when every visible bar is under a day
        const maxHours = Math.max(0, ...visible.flatMap(x => x.series.data.map(v => v ?? 0)));
        this.yAxisUnit = maxHours < 24 ? 'hours' : 'days';
        // hours mode rounds up to whole hours so sub-hour bars match the tooltip
        const scale = (h: number) => this.yAxisUnit === 'hours' ? Math.ceil(h) : h / 24;

        this.chartData = {
            labels: this.monthLabels,
            datasets: visible.map(x => ({
                label: x.series.label,
                data: x.series.data.map(v => scale(v ?? 0)),
                backgroundColor: x.color,
                hoverBackgroundColor: x.color,
                borderRadius: 4,
                maxBarThickness: 40
            }))
        };
    }

    toggleLegendItem(index: number): void {
        if (this.hiddenIndexes.has(index)) {
            this.hiddenIndexes.delete(index);
        } else {
            this.hiddenIndexes.add(index);
        }
        this.updateChartData();
    }

    hasHiddenDatasets(): boolean {
        return this.hiddenIndexes.size > 0;
    }

    resetAllDatasets(): void {
        this.hiddenIndexes.clear();
        this.updateChartData();
    }

    // value is expressed in days
    formatHoursToDayHour(value: number): string {
        if (value === 0) return '0';
        let label = '';
        const hours = Math.ceil(value * 24);
        const days = Math.floor(hours / 24);
        if (days > 0) {
            label += days + (days === 1 ? ' day' : ' days');
        }
        const remainingHours = hours % 24;
        if (remainingHours > 0) {
            if (label) label += ' ';
            label += remainingHours + (remainingHours === 1 ? ' hour' : ' hours');
        }
        return label;
    }
}
